<script setup lang="ts">
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue"
import { CheckCircle, AlertTriangle, X } from 'lucide-vue-next'

interface Props {
  isOpen: boolean
  type: 'success' | 'error' | 'info'
  title: string
  message: string
  buttonText?: string
  showCloseButton?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  buttonText: 'Close',
  showCloseButton: true,
  type: 'info'
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

function closeDialog() {
  emit('close')
}

const getIconColor = () => {
  switch (props.type) {
    case 'error': return 'text-red-600'
    case 'success': return 'text-green-600'
    default: return 'text-brand-blue'
  }
}

const getIconBgColor = () => {
  switch (props.type) {
    case 'error': return 'bg-red-100'
    case 'success': return 'bg-green-100'
    default: return 'bg-brand-blue/10'
  }
}

const getPrimaryBtnColor = () => {
  switch (props.type) {
    case 'error': return 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    case 'success': return 'bg-brand-blue hover:bg-brand-green focus:ring-brand-blue'
    default: return 'bg-brand-blue hover:bg-brand-green focus:ring-brand-blue'
  }
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeDialog" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-900 bg-opacity-40 pointer-events-none" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto pointer-events-none">
        <div class="flex min-h-full items-center justify-center p-4 text-center pointer-events-none">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all border border-brand-blue/10 pointer-events-auto mx-4"
            >
              <!-- Header close -->
              <div class="flex justify-end -mt-2 -mr-2 mb-2" v-if="showCloseButton">
                <button
                  @click="closeDialog"
                  class="cursor-pointer group w-8 h-8 rounded-full group-hover:bg-emerald-50 grid place-items-center"
                >
                  <X class="size-5 text-slate-500 group-hover:text-emerald-600 group-hover:scale-125" />
                </button>
              </div>

              <!-- Centered content (mirrors BaseConfirmationModal) -->
              <div class="flex flex-col items-center text-center">
                <div class="relative mb-6">
                  <div :class="[
                    'w-16 h-16 flex items-center justify-center rounded-2xl shadow-lg border',
                    getIconBgColor(),
                    'border-brand-blue/20'
                  ]">
                    <CheckCircle v-if="type === 'success'" :class="['w-8 h-8', getIconColor()]" />
                    <AlertTriangle v-else :class="['w-8 h-8', getIconColor()]" />
                  </div>
                  <div class="absolute -top-1 -right-1 w-4 h-4 bg-brand-blue/30 rounded-full animate-pulse"></div>
                  <div class="absolute -bottom-1 -left-1 w-3 h-3 bg-brand-blue/30 rounded-full animate-pulse delay-1000"></div>
                </div>

                <h3 class="text-xl font-bold text-brand-blue mb-2 tracking-tight">{{ title }}</h3>
                <p class="text-gray-600 mb-6 leading-relaxed">{{ message }}</p>

                <div class="w-full">
                  <button
                    @click="closeDialog"
                    :class="[
                      'w-full px-6 py-3 text-sm font-semibold text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200',
                      getPrimaryBtnColor()
                    ]"
                  >
                    {{ buttonText }}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>